/**
 * Trust badges + driver tier — pure derivations from a user's stats.
 *
 * See AppFlow §9.1 + Design Brief §5.2 TrustBadge + Gamification design doc §2/§5.
 *
 * V1 launch set (this file): 4 existing badges + 3 new compute-on-query badges
 * (consistent, first_ride, early_supporter), plus a 5-tier driver progression
 * label. No DB schema changes — everything derived from the existing users
 * stats columns.
 */

export type TrustBadgeId =
  | 'founding_driver'
  | 'trusted_route_regular'
  | 'community_verified'
  | 'premium'
  | 'consistent'
  | 'first_ride'
  | 'early_supporter';

export type DriverTier = 'newcomer' | 'regular' | 'reliable' | 'trusted' | 'ambassador';

export interface DriverTierInfo {
  tier: DriverTier;
  /** Numeric 0-4 for convenience. */
  level: 0 | 1 | 2 | 3 | 4;
  /** Localization key, e.g. `tier.regular`. */
  labelKey: string;
}

export interface TrustBadgeInput {
  /** Insertion order: drivers 1..100 are "founding". */
  driverRank?: number | null;
  ridesCompleted: number;
  ratingAvg: number | null;
  isPremium: boolean;
  /** Map of routeId → completed-ride count for that route. */
  ridesPerRoute?: Record<string, number>;
  /** Cancellations in trailing 30 days. From users.cancellation_count_30d. */
  cancellationCount30d?: number;
  /** Unix ms timestamp the user signed up. From users.created_at. */
  accountCreatedAtMs?: number;
  /** Platform launch timestamp in unix ms — used for early_supporter cutoff. */
  platformLaunchAtMs?: number;
}

/**
 * Date the public Waylo Mini App launched. early_supporter window =
 * created_at within 30 days after this. We pin a calendar date rather than
 * a relative offset so the badge is stable forever.
 */
export const PLATFORM_LAUNCH_AT_MS = Date.UTC(2026, 4, 19); // 2026-05-19 UTC
const EARLY_SUPPORTER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function deriveBadges(input: TrustBadgeInput): TrustBadgeId[] {
  const badges: TrustBadgeId[] = [];

  if (typeof input.driverRank === 'number' && input.driverRank > 0 && input.driverRank <= 100) {
    badges.push('founding_driver');
  }

  if (input.ridesCompleted >= 10 && (input.ratingAvg ?? 0) >= 4.7) {
    badges.push('community_verified');
  }

  if (input.ridesPerRoute) {
    const hasRegularRoute = Object.values(input.ridesPerRoute).some((n) => n >= 10);
    if (hasRegularRoute) badges.push('trusted_route_regular');
  }

  // B-05 Consistent: ≥5 completed rides AND 0 cancellations in trailing 30d.
  // (The "90d" window from the design doc is approximated here by the
  // existing cancellation_count_30d aggregate column; we can swap to a
  // computed 90d window when the gamification table lands in V1.1.)
  if (input.ridesCompleted >= 5 && (input.cancellationCount30d ?? 0) === 0) {
    badges.push('consistent');
  }

  // B-10 First Ride: ≥1 completed ride. Quiet welcome marker.
  if (input.ridesCompleted >= 1) {
    badges.push('first_ride');
  }

  // B-16 Early Supporter: signed up within 30 days of public launch
  // AND has completed ≥1 ride (to prevent account-squat farming).
  const launchAt = input.platformLaunchAtMs ?? PLATFORM_LAUNCH_AT_MS;
  if (
    typeof input.accountCreatedAtMs === 'number' &&
    input.accountCreatedAtMs >= launchAt &&
    input.accountCreatedAtMs < launchAt + EARLY_SUPPORTER_WINDOW_MS &&
    input.ridesCompleted >= 1
  ) {
    badges.push('early_supporter');
  }

  if (input.isPremium) badges.push('premium');

  return badges;
}

export interface DriverTierInput {
  isDriver: boolean;
  ridesCompleted: number;
  ratingAvg: number | null;
}

/**
 * Pure derivation. The thresholds match Gamification Design Doc §5.2.
 * No "substantiated reports" criterion yet — that requires a reports
 * adjudication flag we haven't added. V1 uses rides + rating only.
 */
export function deriveDriverTier(input: DriverTierInput): DriverTierInfo {
  if (!input.isDriver) {
    return { tier: 'newcomer', level: 0, labelKey: 'tier.newcomer' };
  }
  const rides = input.ridesCompleted;
  const rating = input.ratingAvg ?? 0;
  if (rides >= 75 && rating >= 4.8) {
    return { tier: 'ambassador', level: 4, labelKey: 'tier.ambassador' };
  }
  if (rides >= 30 && rating >= 4.7) {
    return { tier: 'trusted', level: 3, labelKey: 'tier.trusted' };
  }
  if (rides >= 15 && rating >= 4.5) {
    return { tier: 'reliable', level: 2, labelKey: 'tier.reliable' };
  }
  if (rides >= 5 && rating >= 4.0) {
    return { tier: 'regular', level: 1, labelKey: 'tier.regular' };
  }
  return { tier: 'newcomer', level: 0, labelKey: 'tier.newcomer' };
}

/**
 * Returns the next-tier target (rides, rating) for the supplied driver,
 * or null if already at the top tier. Used on self-profile to display
 * progress toward the next milestone.
 */
export function nextTierTarget(input: DriverTierInput): {
  nextTier: DriverTier;
  ridesNeeded: number;
  ratingNeeded: number;
} | null {
  const current = deriveDriverTier(input);
  switch (current.tier) {
    case 'newcomer':
      return { nextTier: 'regular', ridesNeeded: 5, ratingNeeded: 4.0 };
    case 'regular':
      return { nextTier: 'reliable', ridesNeeded: 15, ratingNeeded: 4.5 };
    case 'reliable':
      return { nextTier: 'trusted', ridesNeeded: 30, ratingNeeded: 4.7 };
    case 'trusted':
      return { nextTier: 'ambassador', ridesNeeded: 75, ratingNeeded: 4.8 };
    case 'ambassador':
      return null;
  }
}

export function isPremiumActive(premiumUntilMs: number | null): boolean {
  if (premiumUntilMs == null) return false;
  return premiumUntilMs > Date.now();
}
